/**
 * Génération de relevés de correction avec OCR Manuscrit V2
 * Permet de choisir le format (cases séparées, zone guidée, simple)
 * Interface complète avec gestion des relevés (aperçu, upload, OCR, suppression)
 */

import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGenererReleveManuscritMutation,
  useLancerOCRManuscritV2Mutation,
  useGetOptionsAvecCopiesQuery,
  useAjouterCopieReleveMutation,
} from '../../services/ocrApiV2';
import {
  useGetRelevesCorrectionQuery,
  useUploadScanCorrectionMutation,
  useDeleteReleveCorrectionMutation,
  useRetournerReleveCorrectionMutation,
  useGetTraitementsOCRQuery,
  useGetLignesOCRCorrectionQuery,
  ReleveCorrection,
} from '../../services/ocrApi';
import { useGetMatiereEnseignantQuery } from '../../services/matieresApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import { useGetGenerationVerrousQuery } from '../../services/coreApi';
import ScrollToTop from '../../components/ui/ScrollToTop';
import NiveauBanner, { getNiveauStyle } from '../../components/ui/NiveauBanner';
import UploadBatchParQRModal from '../../components/Releves/UploadBatchParQRModal';
import HistoriqueImportsModal from '../../components/Releves/HistoriqueImportsModal';
import {
  FileText,
  Download,
  Upload,
  Play,
  CheckCircle2,
  Eye,
  Loader,
  AlertTriangle,
  Sparkles,
  FileCheck,
  ListChecks,
  FileEdit,
  Zap,
  Search,
  Trash2,
  FileSearch,
  ExternalLink,
  RefreshCw,
  XCircle,
  Printer,
  Clock,
  BarChart3,
  X,
  Plus,
  UserCheck,
  RotateCcw,
  User,
  Lock,
} from 'lucide-react';

type NiveauType = 'L1' | 'L2' | 'L3' | 'M1' | 'M2';
type TypeExamenType = 'normal' | 'rattrapage';
type FormatReleveType = 'cases_separees' | 'zone_guidee' | 'simple';

const formatLabels: Record<FormatReleveType, {
  label: string;
  description: string;
  icon: React.ReactNode;
  badge: string;
}> = {
  cases_separees: {
    label: 'Cases Séparées',
    description: 'Un chiffre par case (ex: [1][5][,][5]). Meilleure précision OCR.',
    icon: <ListChecks size={20} />,
    badge: 'RECOMMANDÉ',
  },
  zone_guidee: {
    label: 'Zone Guidée',
    description: 'Zone avec ligne pour écrire + case de vérification.',
    icon: <FileEdit size={20} />,
    badge: 'STANDARD',
  },
  simple: {
    label: 'Simple',
    description: 'Zone libre pour écriture manuscrite.',
    icon: <FileCheck size={20} />,
    badge: 'BASIQUE',
  },
};

const statusLabels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  genere: { label: 'Généré', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300', icon: <FileText size={14} /> },
  imprime: { label: 'Imprimé', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300', icon: <Printer size={14} /> },
  rempli: { label: 'Rempli', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300', icon: <FileText size={14} /> },
  scanne: { label: 'Scanné', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300', icon: <Upload size={14} /> },
  traite: { label: 'Traité', color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300', icon: <Play size={14} /> },
  verifie: { label: 'Vérifié', color: 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300', icon: <Eye size={14} /> },
  valide: { label: 'Validé', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300', icon: <CheckCircle2 size={14} /> },
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const GenerationReleveCorrectionV2: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State formulaire
  const [selectedNiveau, setSelectedNiveau] = useState<NiveauType | ''>('');
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedPoles, setSelectedPoles] = useState<string[]>([]);
  const [selectedSemestre, setSelectedSemestre] = useState<string>('');
  const [selectedMatiere, setSelectedMatiere] = useState<string>('');
  const [selectedEnseignantId, setSelectedEnseignantId] = useState<number | null>(null);
  const [typeExamen, setTypeExamen] = useState<TypeExamenType>('normal');

  // Verrous admin : si actifs, forcent type_examen et parité semestre — évite
  // les erreurs des agents (générer un relevé sur l'ancienne session/semestre).
  const { data: genVerrous } = useGetGenerationVerrousQuery();
  const verrouTypeExamen = (genVerrous?.type_examen || '') as '' | TypeExamenType;
  const verrouParity = (genVerrous?.semestre_parity || '') as '' | 'impair' | 'pair';
  // Aligner automatiquement les states sur la valeur verrouillée dès qu'elle change
  useEffect(() => {
    if (verrouTypeExamen && verrouTypeExamen !== typeExamen) {
      setTypeExamen(verrouTypeExamen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verrouTypeExamen]);
  // La parité verrouille les 2 filtres (relevés générés + relevés traités)
  useEffect(() => {
    if (verrouParity) {
      // Note : setReleveSemestreParity / setTraiteSemestreParity sont déclarés
      // plus bas dans le composant — on les met à jour via effect séparé
      // au moment du changement de verrou.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verrouParity]);
  const [enseignantNom, setEnseignantNom] = useState('');
  const [formatReleve, setFormatReleve] = useState<FormatReleveType>('cases_separees');

  // State modals
  const [selectedReleveForUpload, setSelectedReleveForUpload] = useState<ReleveCorrection | null>(null);
  const [selectedReleveForDetails, setSelectedReleveForDetails] = useState<ReleveCorrection | null>(null);
  const [selectedReleveForDelete, setSelectedReleveForDelete] = useState<ReleveCorrection | null>(null);
  const [selectedReleveForRetour, setSelectedReleveForRetour] = useState<ReleveCorrection | null>(null);
  const [retourSupprimerScan, setRetourSupprimerScan] = useState(false);
  const [retourMotif, setRetourMotif] = useState('');
  const [selectedReleveForAddCopie, setSelectedReleveForAddCopie] = useState<ReleveCorrection | null>(null);
  const [addCopieCode, setAddCopieCode] = useState('');
  const [addCopieConflit, setAddCopieConflit] = useState<any>(null);
  const [showForceConfirm, setShowForceConfirm] = useState<any>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  // Messages
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Filtres relevés — liste principale (en cours)
  const [releveSearch, setReleveSearch] = useState('');
  const [releveStatutFilter, setReleveStatutFilter] = useState('');
  const [releveNiveauFilter, setReleveNiveauFilter] = useState('');
  const [releveSessionFilter, setReleveSessionFilter] = useState<'' | 'normal' | 'rattrapage'>('');
  const [releveSemestreParity, setReleveSemestreParity] = useState<'' | 'impair' | 'pair'>('');

  // Filtres relevés — liste traités/vérifiés/validés
  const [traiteSearch, setTraiteSearch] = useState('');
  const [traiteStatutFilter, setTraiteStatutFilter] = useState('');
  const [traiteNiveauFilter, setTraiteNiveauFilter] = useState('');
  const [traiteSessionFilter, setTraiteSessionFilter] = useState<'' | 'normal' | 'rattrapage'>('');
  const [traiteSemestreParity, setTraiteSemestreParity] = useState<'' | 'impair' | 'pair'>('');

  // Alignement des filtres parité sur le verrou admin (dès qu'il change)
  useEffect(() => {
    if (verrouParity) {
      if (releveSemestreParity !== verrouParity) setReleveSemestreParity(verrouParity);
      if (traiteSemestreParity !== verrouParity) setTraiteSemestreParity(verrouParity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verrouParity]);
  // Consultation scan + saisies
  const [consultReleve, setConsultReleve] = useState<ReleveCorrection | null>(null);

  // Onglet actif pour les listes de relevés
  const [activeListTab, setActiveListTab] = useState<'actifs' | 'traites'>('actifs');

  // Modal d'upload en lot par QR
  const [showUploadBatchQR, setShowUploadBatchQR] = useState(false);
  // Modal historique des imports
  const [showHistoriqueImports, setShowHistoriqueImports] = useState(false);

  // Section "Générer un nouveau relevé" : pliable, repliée par défaut.
  // L'utilisateur arrive directement sur la liste des relevés ; il déplie
  // uniquement quand il veut générer un nouveau relevé.
  const [generationOpen, setGenerationOpen] = useState<boolean>(
    () => localStorage.getItem('rc_generationOpen') === 'true'
  );
  useEffect(() => {
    localStorage.setItem('rc_generationOpen', String(generationOpen));
  }, [generationOpen]);

  // Recherche matière
  const [matiereSearch, setMatiereSearch] = useState('');
  const [showMatiereDropdown, setShowMatiereDropdown] = useState(false);

  // OCR tracking avec timer
  const [ocrEnCours, setOcrEnCours] = useState<Set<number>>(new Set());
  const [ocrTimers, setOcrTimers] = useState<Record<number, number>>({});
  const [, setTick] = useState(0);

  const formatTimer = (startTime: number) => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
  };

  // Queries
  const { data: copiesData } = useGetOptionsAvecCopiesQuery();
  const { data: releves, refetch: refetchReleves, isLoading: relevesLoading } = useGetRelevesCorrectionQuery();
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: allClassesBDD = [] } = useGetClassesQuery({ actif: true });

  // Map { code_classe → { pole, parcours } } pour déduire pôle/parcours depuis les classes
  const classeMetaMap = useMemo(() => {
    const m: Record<string, { pole: string; parcours: string }> = {};
    for (const c of allClassesBDD as any[]) {
      if (c.code) m[c.code] = {
        pole: c.pole_nom || '',
        parcours: c.parcours_nom || '',
      };
    }
    return m;
  }, [allClassesBDD]);

  const getPolesFromClasses = (classesStr: string): string[] => {
    if (!classesStr) return [];
    const classes = classesStr.split(',').map(c => c.trim()).filter(Boolean);
    const poles = new Set<string>();
    for (const cls of classes) {
      const meta = classeMetaMap[cls];
      if (meta?.pole) poles.add(meta.pole);
    }
    return Array.from(poles).sort();
  };

  const getParcoursFromClasses = (classesStr: string): string => {
    if (!classesStr) return '— Sans parcours —';
    const classes = classesStr.split(',').map(c => c.trim()).filter(Boolean);
    const parcours = new Set<string>();
    for (const cls of classes) {
      const meta = classeMetaMap[cls];
      if (meta?.parcours) parcours.add(meta.parcours);
    }
    if (parcours.size === 0) return '— Sans parcours —';
    return Array.from(parcours).sort().join(' / ');
  };

  const matiereIdNum = selectedMatiere ? parseInt(selectedMatiere) : 0;
  const { data: enseignantData } = useGetMatiereEnseignantQuery(
    { matiereId: matiereIdNum, anneeAcademique: anneeActive?.libelle },
    { skip: !selectedMatiere || !matiereIdNum || isNaN(matiereIdNum) || !anneeActive }
  );

  // Mutations
  const [genererReleve, { isLoading: genererLoading }] = useGenererReleveManuscritMutation();
  const [uploadScan, { isLoading: uploadLoading }] = useUploadScanCorrectionMutation();
  const [lancerOCR, { isLoading: ocrLoading }] = useLancerOCRManuscritV2Mutation();
  const [deleteReleve, { isLoading: deleteLoading }] = useDeleteReleveCorrectionMutation();
  const [retournerReleve, { isLoading: retournerLoading }] = useRetournerReleveCorrectionMutation();
  const [ajouterCopie, { isLoading: ajoutCopieLoading }] = useAjouterCopieReleveMutation();

  // Traitements OCR du relevé sélectionné (pour afficher la durée dans les détails)
  const { data: traitementsReleve } = useGetTraitementsOCRQuery(
    { releve_correction: selectedReleveForDetails?.id },
    { skip: !selectedReleveForDetails }
  );
  const dernierTraitement = traitementsReleve
    ?.filter(t => t.releve_correction === selectedReleveForDetails?.id && t.status === 'termine')
    .sort((a, b) => (b.id - a.id))[0];

  // Semestres disponibles
  const getSemestresForNiveau = (niveau: string) => {
    if (niveau === 'L1') return [{ value: '1', label: 'S1' }, { value: '2', label: 'S2' }];
    if (niveau === 'L2') return [{ value: '3', label: 'S3' }, { value: '4', label: 'S4' }];
    if (niveau === 'L3') return [{ value: '5', label: 'S5' }, { value: '6', label: 'S6' }];
    return [{ value: '1', label: 'S1' }, { value: '2', label: 'S2' }];
  };

  const semestresDisponibles = useMemo(() => getSemestresForNiveau(selectedNiveau), [selectedNiveau]);

  // Niveaux et options disponibles (uniquement ceux ayant des copies associées)
  const niveauxDisponibles = useMemo(() => copiesData?.niveaux || [], [copiesData]);

  // Filtrer matières par niveau (toutes options confondues)
  // Timer tick pour l'OCR
  useEffect(() => {
    if (ocrEnCours.size === 0) return;
    const tickInterval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(tickInterval);
  }, [ocrEnCours.size]);

  // Polling OCR
  useEffect(() => {
    if (ocrEnCours.size === 0) return;
    const interval = setInterval(() => {
      refetchReleves().then(({ data }) => {
        if (!data || !Array.isArray(data)) return;
        const terminés: number[] = [];
        Array.from(ocrEnCours).forEach(id => {
          const rel = data.find((r: any) => r.id === id);
          if (rel && rel.status === 'traite') terminés.push(id);
        });
        if (terminés.length > 0) {
          setOcrEnCours(prev => { const next = new Set(prev); terminés.forEach(id => next.delete(id)); return next; });
          const durees = terminés.map(id => ocrTimers[id] ? formatTimer(ocrTimers[id]) : '').filter(Boolean);
          setOcrTimers(prev => { const next = { ...prev }; terminés.forEach(id => delete next[id]); return next; });
          setMessage({ type: 'success', text: `OCR terminé${durees.length ? ` en ${durees.join(', ')}` : ''} !` });
        }
      });
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocrEnCours.size]);

  const filteredMatieres = useMemo(() => {
    if (!copiesData || !selectedNiveau) return [];
    let result = copiesData.matieres.filter((m: any) => m.niveau === selectedNiveau);
    if (matiereSearch) {
      const q = matiereSearch.toLowerCase();
      result = result.filter((m: any) =>
        m.nom_matiere.toLowerCase().includes(q) ||
        m.classes?.some((c: string) => c.toLowerCase().includes(q)) ||
        m.enseignants?.some((e: any) => e.nom.toLowerCase().includes(q))
      );
    }
    return result;
  }, [copiesData, selectedNiveau, matiereSearch]);

  // Filtrer les relevés générés avec V2
  const relevesV2All = useMemo(() => {
    return releves?.filter(r =>
      r.format_releve && r.format_releve.startsWith('manuscrit_')
    ) || [];
  }, [releves]);

  // Statuts considérés comme "traités" (ne doivent plus apparaître dans la liste principale)
  const STATUTS_TRAITES = ['traite', 'verifie', 'valide'];

  // Séparer en deux ensembles : actifs (en cours) vs traités
  const relevesActifsAll = useMemo(
    () => relevesV2All.filter(r => !STATUTS_TRAITES.includes(r.status)),
    [relevesV2All]
  );
  const relevesTraitesAll = useMemo(
    () => relevesV2All.filter(r => STATUTS_TRAITES.includes(r.status)),
    [relevesV2All]
  );

  // Helper de recherche textuelle
  const matchSearch = (r: ReleveCorrection, q: string) => {
    const lc = q.toLowerCase();
    return (
      r.code_releve.toLowerCase().includes(lc) ||
      (r.matiere_nom || '').toLowerCase().includes(lc) ||
      (r.matiere_code || '').toLowerCase().includes(lc) ||
      (r.classes || '').toLowerCase().includes(lc) ||
      (r.enseignant_nom || '').toLowerCase().includes(lc) ||
      (r.option || '').toLowerCase().includes(lc)
    );
  };

  // Parité d'un semestre : impair (1,3,5) ou pair (2,4,6).
  const semestreParity = (semestre: string | number | undefined | null): 'impair' | 'pair' | null => {
    if (!semestre) return null;
    const s = String(semestre).replace(/\D/g, '');
    const n = parseInt(s, 10);
    if (isNaN(n)) return null;
    return n % 2 === 1 ? 'impair' : 'pair';
  };

  // Appliquer les filtres — liste principale (en cours : genere, imprime, rempli, scanne)
  const relevesV2 = useMemo(() => {
    let result = relevesActifsAll;
    if (releveStatutFilter) result = result.filter(r => r.status === releveStatutFilter);
    if (releveNiveauFilter) result = result.filter(r => r.niveau === releveNiveauFilter);
    if (releveSessionFilter) result = result.filter(r => (r.type_examen || 'normal') === releveSessionFilter);
    if (releveSemestreParity) result = result.filter(r => semestreParity(r.semestre) === releveSemestreParity);
    if (releveSearch) result = result.filter(r => matchSearch(r, releveSearch));
    return result;
  }, [relevesActifsAll, releveSearch, releveStatutFilter, releveNiveauFilter, releveSessionFilter, releveSemestreParity]);

  // Appliquer les filtres — liste traités
  const relevesTraites = useMemo(() => {
    let result = relevesTraitesAll;
    if (traiteStatutFilter) result = result.filter(r => r.status === traiteStatutFilter);
    if (traiteNiveauFilter) result = result.filter(r => r.niveau === traiteNiveauFilter);
    if (traiteSessionFilter) result = result.filter(r => (r.type_examen || 'normal') === traiteSessionFilter);
    if (traiteSemestreParity) result = result.filter(r => semestreParity(r.semestre) === traiteSemestreParity);
    if (traiteSearch) result = result.filter(r => matchSearch(r, traiteSearch));
    return result;
  }, [relevesTraitesAll, traiteSearch, traiteStatutFilter, traiteNiveauFilter, traiteSessionFilter, traiteSemestreParity]);

  // Niveaux des relevés pour les filtres
  const niveauxReleves = useMemo(() => {
    return Array.from(new Set(relevesActifsAll.map(r => r.niveau))).sort();
  }, [relevesActifsAll]);
  const niveauxTraites = useMemo(() => {
    return Array.from(new Set(relevesTraitesAll.map(r => r.niveau))).sort();
  }, [relevesTraitesAll]);

  // Grouper les relevés par niveau uniquement
  const relevesGroupes = useMemo(() => {
    const groups: Record<string, typeof relevesV2> = {};
    for (const r of relevesV2) {
      const niveau = r.niveau || '— Sans niveau —';
      if (!groups[niveau]) groups[niveau] = [];
      groups[niveau].push(r);
    }
    // Trier les niveaux : L1, L2, L3, M1, M2, autres
    const niveauOrder = ['L1', 'L2', 'L3', 'M1', 'M2'];
    return Object.keys(groups)
      .sort((a, b) => {
        const ia = niveauOrder.indexOf(a);
        const ib = niveauOrder.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      })
      .map(niveau => ({ niveau, releves: groups[niveau] }));
  }, [relevesV2]);

  // Grouper les relevés traités par niveau
  const relevesTraitesGroupes = useMemo(() => {
    const groups: Record<string, typeof relevesTraites> = {};
    for (const r of relevesTraites) {
      const niveau = r.niveau || '— Sans niveau —';
      if (!groups[niveau]) groups[niveau] = [];
      groups[niveau].push(r);
    }
    const niveauOrder = ['L1', 'L2', 'L3', 'M1', 'M2'];
    return Object.keys(groups)
      .sort((a, b) => {
        const ia = niveauOrder.indexOf(a);
        const ib = niveauOrder.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      })
      .map(niveau => ({ niveau, releves: groups[niveau] }));
  }, [relevesTraites]);

  // Etat ouvert/fermé des groupes (par défaut tout ouvert)
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Statistiques (globales sur tous les relevés — pas filtrées)
  const stats = useMemo(() => {
    const total = relevesV2All.length;
    const generes = relevesV2All.filter(r => r.status === 'genere').length;
    const scannes = relevesV2All.filter(r => r.status === 'scanne').length;
    const aVerifier = relevesV2All.filter(r => ['traite', 'verifie'].includes(r.status)).length;
    const valides = relevesV2All.filter(r => r.status === 'valide').length;
    return { total, generes, scannes, aVerifier, valides };
  }, [relevesV2All]);

  // Mettre à jour enseignant
  useEffect(() => {
    if (enseignantData?.enseignant_nom) {
      setEnseignantNom(enseignantData.enseignant_nom);
    }
  }, [enseignantData]);

  // Auto-dismiss message
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // --- Handlers ---

  const handleGenererReleve = async (force = false) => {
    if (!selectedMatiere || !selectedNiveau || !anneeActive) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un niveau et une matière' });
      return;
    }

    const mat = filteredMatieres.find((m: any) => m.key === selectedMatiere);
    if (!mat) return;

    // Vérifier qu'au moins un pôle est sélectionné si plusieurs pôles
    if (mat.poles && mat.poles.length > 1 && selectedPoles.length === 0) {
      setMessage({ type: 'error', text: `Cette matière concerne ${mat.poles.length} pôles (${mat.poles.join(', ')}). Sélectionnez au moins un pôle.` });
      return;
    }

    try {
      const semestreApi = mat.semestre % 2 === 1 ? 'S1' : 'S2';

      const result = await genererReleve({
        matiere_id: mat.matiere_ids[0],
        matiere_ids: mat.matiere_ids,
        niveau: selectedNiveau,
        option: mat.options?.[0] || '',
        semestre: semestreApi,
        annee_academique: anneeActive.libelle,
        type_examen: typeExamen,
        enseignant_nom: enseignantNom,
        enseignant_id: selectedEnseignantId || undefined,
        format: formatReleve,
        poles: selectedPoles.length > 0 ? selectedPoles : undefined,
        force,
      } as any).unwrap();

      setMessage({
        type: 'success',
        text: `Relevé généré avec succès: ${result.code_releve} (Format: ${formatLabels[formatReleve].label})`,
      });
      setShowForceConfirm(null);
      refetchReleves();
      setSelectedMatiere('');
    } catch (error: any) {
      if (error?.status === 409 && error?.data?.releve_existant) {
        const existant = error.data.releve_existant;
        setShowForceConfirm(existant);
        setMessage({
          type: 'error',
          text: error?.data?.error || `Un relevé identique existe déjà (${existant.code_releve}).`,
        });
      } else {
        setMessage({
          type: 'error',
          text: error?.data?.error || 'Erreur lors de la génération du relevé',
        });
      }
    }
  };

  const handleUploadScan = async () => {
    if (!selectedReleveForUpload || !uploadFile) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('fichier', uploadFile);

      await uploadScan({ releveId: selectedReleveForUpload.id, formData }).unwrap();

      setMessage({ type: 'success', text: `Scan uploadé avec succès pour ${selectedReleveForUpload.code_releve}` });
      setSelectedReleveForUpload(null);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || "Erreur lors de l'upload",
      });
    }
  };

  const handleLancerOCR = async (releve: ReleveCorrection) => {
    try {
      const result = await lancerOCR(releve.id).unwrap();

      if (result.methode === 'synchrone' && result.result) {
        setMessage({
          type: 'success',
          text: `OCR terminé: ${result.result.total_notes} notes (${result.result.notes_haute_confiance} OK, ${result.result.notes_a_verifier} à vérifier) en ${result.result.duree_secondes.toFixed(1)}s`,
        });
      } else {
        // Tracking OCR asynchrone
        setOcrEnCours(prev => { const next = new Set(prev); next.add(releve.id); return next; });
        setOcrTimers(prev => ({ ...prev, [releve.id]: Date.now() }));
        setMessage({
          type: 'success',
          text: 'Traitement OCR lancé...',
        });
      }

      refetchReleves();

      setTimeout(() => {
        navigate(`/ocr/verification-correction-v2/${releve.id}`);
      }, 2000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || "Erreur lors du lancement de l'OCR",
      });
    }
  };

  const handleRetournerEnCours = async () => {
    if (!selectedReleveForRetour) return;
    try {
      const res = await retournerReleve({
        releveId: selectedReleveForRetour.id,
        supprimer_scan: retourSupprimerScan,
        motif: retourMotif.trim(),
      }).unwrap();
      setMessage({ type: 'success', text: res.message });
      setSelectedReleveForRetour(null);
      setRetourSupprimerScan(false);
      setRetourMotif('');
      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors du retour en cours',
      });
    }
  };

  const handleDeleteReleve = async () => {
    if (!selectedReleveForDelete) return;

    try {
      await deleteReleve(selectedReleveForDelete.id).unwrap();
      setMessage({ type: 'success', text: `Relevé ${selectedReleveForDelete.code_releve} supprimé` });
      setSelectedReleveForDelete(null);
      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la suppression',
      });
    }
  };

  const handleAjouterCopie = async (force: boolean) => {
    if (!selectedReleveForAddCopie || !addCopieCode.trim()) return;
    try {
      const res = await ajouterCopie({
        releveId: selectedReleveForAddCopie.id,
        code_copie: addCopieCode.trim(),
        force,
      }).unwrap();

      if (res.success) {
        setMessage({ type: 'success', text: res.message || 'Copie ajoutée' });
        setAddCopieCode('');
        setAddCopieConflit(null);
        refetchReleves();
      }
    } catch (error: any) {
      const data = error?.data;
      if (data?.conflit) {
        // Conflit détecté → afficher les détails
        setAddCopieConflit(data.conflit);
      } else {
        setMessage({ type: 'error', text: data?.error || data?.message || 'Erreur lors de l\'ajout' });
      }
    }
  };

  const handleDownloadPDF = (releve: ReleveCorrection) => {
    if (releve.fichier_pdf_genere) {
      window.open(releve.fichier_pdf_genere, '_blank');
    }
  };

  const handlePreviewPDF = (releve: ReleveCorrection) => {
    if (releve.fichier_pdf_genere) {
      window.open(releve.fichier_pdf_genere, '_blank');
    }
  };

  /**
   * Impression groupée d'un ensemble de relevés (tous PDFs empilés dans une
   * fenêtre prête à imprimer via Ctrl+P).
   */
  const handleBulkPrint = (releves: ReleveCorrection[], titre: string) => {
    const items = releves
      .filter(r => r.fichier_pdf_genere)
      .map(r => ({ code: r.code_releve, url: r.fichier_pdf_genere as string }));

    if (items.length === 0) {
      alert('Aucun PDF disponible parmi les relevés sélectionnés');
      return;
    }
    if (!window.confirm(`Préparer l'impression groupée de ${items.length} relevé(s) (${titre}) ?\n\nUne fenêtre va s'ouvrir avec tous les PDFs empilés. Lance Ctrl+P pour tout imprimer.`)) return;

    const win = window.open('', '_blank');
    if (!win) {
      alert('Le popup a été bloqué — autorise les popups pour ce site');
      return;
    }
    const html = `
      <!DOCTYPE html>
      <html><head><meta charset="utf-8"><title>Impression : ${titre}</title>
      <style>
        body { margin: 0; padding: 0; background: #525659; }
        .topbar { position: sticky; top: 0; background: #1f2937; color: #fff; padding: 12px 20px; z-index: 100; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 8px rgba(0,0,0,.3); }
        .topbar h1 { margin: 0; font: 600 16px sans-serif; }
        .topbar button { background: #2563eb; color: #fff; border: 0; padding: 8px 16px; border-radius: 6px; font: 600 14px sans-serif; cursor: pointer; }
        .topbar button:hover { background: #1d4ed8; }
        .pdf-item .label { background: #fff; padding: 8px 16px; font: 600 14px sans-serif; color: #111; border-bottom: 1px solid #e5e7eb; }
        .pdf-item iframe { width: 100%; height: 100vh; border: 0; display: block; background: #fff; }
        @media print {
          .topbar, .pdf-item .label { display: none !important; }
          body { background: #fff; }
          .pdf-item { page-break-after: always; }
        }
      </style></head><body>
        <div class="topbar">
          <h1>${titre} — ${items.length} relevé(s)</h1>
          <button onclick="window.print()">🖨️ Imprimer tout (Ctrl+P)</button>
        </div>
        ${items.map(it => `
          <div class="pdf-item">
            <div class="label">${it.code}</div>
            <iframe src="${it.url}#toolbar=0&view=FitH"></iframe>
          </div>
        `).join('')}
      </body></html>
    `;
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto dark:bg-gray-900 min-h-screen">
      {/* Bandeau verrouillage admin (visible en tête pour clarté) */}
      {(verrouTypeExamen || verrouParity) && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-700 flex items-start gap-3 text-sm">
          <Lock size={18} className="text-red-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-red-900 dark:text-red-200">
            <div className="font-semibold">Verrouillage administrateur actif</div>
            <div className="text-xs mt-1">
              {verrouTypeExamen && (
                <span className="inline-block mr-3">
                  Type d'examen forcé : <strong>{verrouTypeExamen === 'normal' ? 'Session Ordinaire' : 'Rattrapage'}</strong>
                </span>
              )}
              {verrouParity && (
                <span className="inline-block mr-3">
                  Semestres forcés : <strong>{verrouParity === 'impair' ? 'Impairs (S1/S3/S5)' : 'Pairs (S2/S4/S6)'}</strong>
                </span>
              )}
              <span className="italic">Modifier dans Paramètres → Verrouillage Génération relevés correction V2.</span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Sparkles className="text-blue-600" size={32} />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Relevés Examens - Cloud Vision OCR
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                Reconnaissance automatique des notes manuscrites via Google Cloud Vision
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => refetchReleves()}
              className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg flex items-center gap-2 transition-all"
              title="Actualiser la liste"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={() => navigate('/ocr/reclamations')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            >
              <Search size={18} />
              Réclamations
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {message.type === 'success' ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
              <span>{message.text}</span>
            </div>
            <button onClick={() => { setMessage(null); setShowForceConfirm(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <XCircle size={18} />
            </button>
          </div>
          {showForceConfirm && (
            <div className="mt-3 flex items-center gap-3 pt-3 border-t border-red-200 dark:border-red-800">
              <span className="text-sm">Ancien relevé : <strong>{showForceConfirm.code_releve}</strong> ({showForceConfirm.status_label})</span>
              <button
                onClick={() => handleGenererReleve(true)}
                disabled={genererLoading}
                className="px-4 py-1.5 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {genererLoading ? <Loader size={14} className="inline animate-spin mr-1" /> : <Trash2 size={14} className="inline mr-1" />}
                Supprimer l'ancien et régénérer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-1">
            <BarChart3 size={16} />
            Total
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center gap-2 text-blue-500 text-sm mb-1">
            <FileText size={16} />
            Générés
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.generes}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center gap-2 text-orange-500 text-sm mb-1">
            <Upload size={16} />
            Scannés
          </div>
          <p className="text-2xl font-bold text-orange-600">{stats.scannes}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center gap-2 text-indigo-500 text-sm mb-1">
            <Eye size={16} />
            A vérifier
          </div>
          <p className="text-2xl font-bold text-indigo-600">{stats.aVerifier}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center gap-2 text-green-500 text-sm mb-1">
            <CheckCircle2 size={16} />
            Validés
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.valides}</p>
        </div>
      </div>

      {/* Formulaire de génération — pliable, replié par défaut */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-6 overflow-hidden">
        {/* Header cliquable */}
        <button
          onClick={() => setGenerationOpen(!generationOpen)}
          className={`w-full flex items-center justify-between px-6 py-4 text-left transition-colors ${
            generationOpen
              ? 'bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${generationOpen ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'} transition-colors`}>
              <FileText size={18} />
            </div>
            <div>
              <h2 className="text-lg font-semibold dark:text-gray-100">Générer un nouveau relevé</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {generationOpen ? 'Cliquez pour replier' : 'Cliquez pour déplier le formulaire de génération'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!generationOpen && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
                + Nouveau
              </span>
            )}
            <span className={`text-gray-400 transition-transform ${generationOpen ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>
        </button>

        {generationOpen && (
        <div className="p-6" style={{ overflow: 'visible' }}>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" style={{ overflow: 'visible' }}>
          {/* Niveau */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau *</label>
            <select
              value={selectedNiveau}
              onChange={(e) => {
                setSelectedNiveau(e.target.value as NiveauType);
                setSelectedSemestre('');
                setSelectedMatiere('');
              }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">Sélectionner...</option>
              {niveauxDisponibles.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          {/* Matière (toutes options confondues) avec recherche */}
          <div className={`md:col-span-2 ${showMatiereDropdown ? 'relative z-[100]' : ''}`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matière *</label>
            <div className="relative">
              {/* Champ de recherche + affichage sélection */}
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={showMatiereDropdown ? matiereSearch : (filteredMatieres.find((m: any) => m.key === selectedMatiere)?.nom_matiere || '')}
                  onChange={(e) => { setMatiereSearch(e.target.value); if (!showMatiereDropdown) setShowMatiereDropdown(true); }}
                  onFocus={() => { setShowMatiereDropdown(true); setMatiereSearch(''); }}
                  placeholder={selectedNiveau ? 'Rechercher une matiere...' : 'Selectionnez d\'abord un niveau'}
                  disabled={!selectedNiveau}
                  className="w-full pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                />
                {selectedMatiere && !showMatiereDropdown && (
                  <button type="button" onClick={() => { setSelectedMatiere(''); setSelectedEnseignantId(null); setEnseignantNom(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={14} /></button>
                )}
              </div>
              {/* Dropdown en overlay fixe */}
              {showMatiereDropdown && selectedNiveau && (
                <>
                  {/* Overlay pour fermer au clic extérieur */}
                  <div className="fixed inset-0 z-[90]" onClick={() => { setShowMatiereDropdown(false); setMatiereSearch(''); }} />
                  <div className="absolute left-0 right-0 top-full mt-1 z-[100] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl max-h-64 overflow-y-auto">
                    {filteredMatieres.length === 0 ? (
                      <div className="p-3 text-center text-xs text-gray-400">Aucune matiere trouvee</div>
                    ) : filteredMatieres.map((m: any) => (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => {
                          setSelectedMatiere(m.key);
                          setSelectedOption(m.options?.[0] || '');
                          setSelectedPoles([]);
                          setSelectedSemestre(String(m.semestre));
                          if (m.enseignants && m.enseignants.length > 0) {
                            setSelectedEnseignantId(m.enseignants[0].id);
                            setEnseignantNom(m.enseignants[0].nom);
                          } else {
                            setSelectedEnseignantId(null);
                            setEnseignantNom('');
                          }
                          setShowMatiereDropdown(false);
                          setMatiereSearch('');
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-700/50 ${
                          selectedMatiere === m.key ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">{m.nom_matiere}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {m.nb_copies} copies | {m.classes?.join(', ')}
                          {m.enseignants?.length > 0 && <span className="ml-1 text-green-600">[{m.enseignants.map((e: any) => e.nom).join(', ')}]</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Infos de la matière sélectionnée */}
            {selectedMatiere && (() => {
              const mat = filteredMatieres.find((m: any) => m.key === selectedMatiere);
              if (!mat) return null;
              return (
                <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {mat.classes?.map((c: string) => (
                      <span key={c} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold">{c}</span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>{mat.nb_copies} copies associées</span>
                    <span>•</span>
                    <span>{mat.options?.join(', ')}</span>
                    {mat.associes_par && mat.associes_par.length > 0 && (
                      <>
                        <span>•</span>
                        <span>par {mat.associes_par.join(', ')}</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Formateur */}
          {selectedMatiere && (() => {
            const mat = filteredMatieres.find((m: any) => m.key === selectedMatiere);
            const enseignants = mat?.enseignants || [];
            return (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Formateur {enseignants.length > 1 && <span className="text-amber-600 text-xs">(⚠ plusieurs formateurs)</span>}
                </label>
                <select
                  value={selectedEnseignantId ?? ''}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    setSelectedEnseignantId(id);
                    const ens = enseignants.find((en: any) => en.id === id);
                    if (ens) setEnseignantNom(ens.nom);
                    else setEnseignantNom('');
                  }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">— Tous (relevé unique) —</option>
                  {enseignants.map((e: any) => (
                    <option key={e.id} value={e.id}>{e.nom}</option>
                  ))}
                </select>
                {enseignants.length > 1 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                    Choisissez un formateur pour un relevé séparé, ou "Tous" pour un relevé unique.
                  </p>
                )}

                {/* Pôles — cases à cocher pour matières multi-pôles */}
                {mat?.poles && mat.poles.length > 1 && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pôles <span className="text-red-500 text-xs">(sélectionnez au moins un — {mat.poles.length} détectés)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {mat.poles.map((p: string) => (
                        <label key={p} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer select-none transition-colors ${
                          selectedPoles.includes(p)
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                            : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600'
                        }`}>
                          <input
                            type="checkbox"
                            checked={selectedPoles.includes(p)}
                            onChange={() => {
                              setSelectedPoles(prev =>
                                prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
                              );
                            }}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 cursor-pointer"
                          />
                          <span className="text-xs font-medium">{p}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Cochez les pôles à inclure dans le relevé. Les enveloppes étant séparées par pôle, sélectionnez ceux qui correspondent.
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Type examen */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
              Type d'examen *
              {verrouTypeExamen && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-600 dark:text-red-400 ml-1">
                  <Lock size={10} /> Verrouillé
                </span>
              )}
            </label>
            <select
              value={typeExamen}
              onChange={(e) => !verrouTypeExamen && setTypeExamen(e.target.value as TypeExamenType)}
              disabled={!!verrouTypeExamen}
              className={`w-full border rounded-lg px-3 py-2 dark:text-gray-100 ${
                verrouTypeExamen
                  ? 'border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-700 cursor-not-allowed'
                  : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700'
              }`}
              title={verrouTypeExamen ? 'Verrouillé par l\'administrateur — modifier dans Paramètres → Verrouillage Génération relevés correction V2' : undefined}
            >
              <option value="normal">Session Ordinaire</option>
              <option value="rattrapage">Rattrapage</option>
            </select>
          </div>

          {/* Enseignant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Enseignant</label>
            <input
              type="text"
              value={enseignantNom}
              onChange={(e) => setEnseignantNom(e.target.value)}
              placeholder="Nom de l'enseignant"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        </div>

        {/* Format du relevé */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Format du relevé *</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(Object.keys(formatLabels) as FormatReleveType[]).map((format) => {
              const info = formatLabels[format];
              return (
                <button
                  key={format}
                  onClick={() => setFormatReleve(format)}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    formatReleve === format
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {info.icon}
                      <span className="font-semibold dark:text-gray-100">{info.label}</span>
                    </div>
                    {info.badge && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        format === 'cases_separees'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {info.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{info.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bouton générer */}
        <div className="mt-6">
          <button
            onClick={() => handleGenererReleve()}
            disabled={genererLoading || !selectedMatiere}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {genererLoading ? (
              <>
                <Loader className="animate-spin" size={20} />
                Génération en cours...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Générer le relevé
              </>
            )}
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">Workflow de traitement</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-300">
            <li>Générez un relevé pour la matière souhaitée</li>
            <li>Téléchargez et imprimez le PDF</li>
            <li>L'enseignant inscrit les notes à la main sur le relevé</li>
            <li>Scannez le relevé rempli (300 DPI, couleur recommandé)</li>
            <li>Uploadez le scan et lancez le traitement OCR (Cloud Vision)</li>
            <li>Vérifiez les notes détectées et validez</li>
          </ol>
        </div>
        </div>
        )}
      </div>

      {/* Liste des relevés V2 — avec onglets */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        {/* Onglets de navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-3">
          <div className="flex items-center">
            <button
              onClick={() => setActiveListTab('actifs')}
              className={`px-5 py-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                activeListTab === 'actifs'
                  ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <Clock size={16} />
              Relevés en cours
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeListTab === 'actifs'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {relevesActifsAll.length}
              </span>
            </button>
            <button
              onClick={() => setActiveListTab('traites')}
              className={`px-5 py-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                activeListTab === 'traites'
                  ? 'border-green-600 text-green-700 dark:text-green-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <CheckCircle2 size={16} />
              Traités / Vérifiés / Validés
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeListTab === 'traites'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {relevesTraitesAll.length}
              </span>
            </button>
          </div>
          <div className="flex items-center gap-2 px-2">
            <button
              onClick={() => setShowUploadBatchQR(true)}
              title="Uploader plusieurs scans à la fois — routage automatique via QR code"
              className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded flex items-center gap-1"
            >
              <Upload size={12} /> Upload en lot (QR)
            </button>
            <button
              onClick={() => setShowHistoriqueImports(true)}
              title="Historique des imports de scans"
              className="px-3 py-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/30 flex items-center gap-1"
            >
              <Clock size={12} /> Historique imports
            </button>
            <button
              onClick={() => refetchReleves()}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1 px-3 py-2"
            >
              <RefreshCw size={14} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Onglet 1 : Relevés en cours */}
        {activeListTab === 'actifs' && (
        <div className="p-4">
        {/* Sous-onglets session : Toutes / Ordinaire / Rattrapage */}
        <div className="mb-3 flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700 pb-2">
          {([
            { key: '', label: 'Toutes', count: relevesActifsAll.length, color: 'gray' },
            { key: 'normal', label: 'Session ordinaire', count: relevesActifsAll.filter(r => (r.type_examen || 'normal') === 'normal').length, color: 'blue' },
            { key: 'rattrapage', label: 'Session de rattrapage', count: relevesActifsAll.filter(r => (r.type_examen || 'normal') === 'rattrapage').length, color: 'orange' },
          ] as const).map(opt => {
            const active = releveSessionFilter === opt.key;
            const colorCls = active
              ? (opt.color === 'blue' ? 'bg-blue-600 text-white border-blue-600'
                : opt.color === 'orange' ? 'bg-orange-600 text-white border-orange-600'
                : 'bg-gray-700 text-white border-gray-700')
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';
            return (
              <button
                key={opt.key || 'all'}
                onClick={() => setReleveSessionFilter(opt.key as any)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors inline-flex items-center gap-1.5 ${colorCls}`}
              >
                {opt.label}
                <span className={`px-1.5 py-0 rounded-full text-[10px] font-bold ${
                  active ? 'bg-white/25' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}>
                  {opt.count}
                </span>
              </button>
            );
          })}
        </div>
        {/* Filtres relevés */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={releveSearch}
              onChange={(e) => setReleveSearch(e.target.value)}
              placeholder="Rechercher par code, matiere, formateur, classe..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <select
            value={releveStatutFilter}
            onChange={(e) => setReleveStatutFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Tous statuts</option>
            <option value="genere">Généré</option>
            <option value="imprime">Imprimé</option>
            <option value="rempli">Rempli</option>
            <option value="scanne">Scanné</option>
          </select>
          <select
            value={releveNiveauFilter}
            onChange={(e) => setReleveNiveauFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Tous niveaux</option>
            {niveauxReleves.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {/* Toggle compact impair/pair avec compteurs — plus visible qu'un select
              pour clairement séparer les 2 blocs (session en cours & rattrapage
              ne se mélangent plus dans la même liste). */}
          {(() => {
            const nbImpair = relevesActifsAll.filter(r => semestreParity(r.semestre) === 'impair').length;
            const nbPair = relevesActifsAll.filter(r => semestreParity(r.semestre) === 'pair').length;
            return (
              <div className={`inline-flex rounded-lg border overflow-hidden text-xs ${
                verrouParity ? 'border-red-400 dark:border-red-700 ring-1 ring-red-200 dark:ring-red-900' : 'border-gray-300 dark:border-gray-600'
              }`}
                title={verrouParity ? 'Verrouillé par admin — Paramètres → Verrouillage Génération V2' : undefined}
              >
                {verrouParity && <Lock size={10} className="text-red-500 self-center mx-1 shrink-0" />}
                <button
                  onClick={() => !verrouParity && setReleveSemestreParity('')}
                  disabled={!!verrouParity}
                  className={`px-2 py-1.5 font-medium ${
                    !releveSemestreParity
                      ? 'bg-gray-800 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                  } ${verrouParity ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  Tous ({nbImpair + nbPair})
                </button>
                <button
                  onClick={() => !verrouParity && setReleveSemestreParity('impair')}
                  disabled={!!verrouParity && verrouParity !== 'impair'}
                  className={`px-2 py-1.5 font-medium border-l border-gray-300 dark:border-gray-600 ${
                    releveSemestreParity === 'impair'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                  } ${verrouParity && verrouParity !== 'impair' ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title="Semestres impairs (S1 / S3 / S5)"
                >
                  Impairs ({nbImpair})
                </button>
                <button
                  onClick={() => !verrouParity && setReleveSemestreParity('pair')}
                  disabled={!!verrouParity && verrouParity !== 'pair'}
                  className={`px-2 py-1.5 font-medium border-l border-gray-300 dark:border-gray-600 ${
                    releveSemestreParity === 'pair'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                  } ${verrouParity && verrouParity !== 'pair' ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title="Semestres pairs (S2 / S4 / S6)"
                >
                  Pairs ({nbPair})
                </button>
              </div>
            );
          })()}
          {(releveSearch || releveStatutFilter || releveNiveauFilter || releveSessionFilter || releveSemestreParity) && (
            <button
              onClick={() => { setReleveSearch(''); setReleveStatutFilter(''); setReleveNiveauFilter(''); setReleveSessionFilter(''); setReleveSemestreParity(''); }}
              className="px-2 py-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Reinitialiser
            </button>
          )}
          <span className="text-[10px] text-gray-400">{relevesV2.length}/{relevesActifsAll.length}</span>
        </div>

        {relevesLoading ? (
          <div className="p-8 text-center">
            <Loader className="animate-spin mx-auto text-blue-600" size={32} />
            <p className="mt-2 text-gray-600 dark:text-gray-400">Chargement...</p>
          </div>
        ) : relevesV2.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <FileText size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p className="text-lg font-medium">Aucun relevé généré</p>
            <p className="text-sm mt-1">Utilisez le formulaire ci-dessus pour créer votre premier relevé</p>
          </div>
        ) : (
          <div>
            {relevesGroupes.map(({ niveau, releves }) => {
              const isCollapsed = collapsedGroups[niveau];
              const ns = getNiveauStyle(niveau);
              return (
                <div key={niveau} className={`border-l-4 ${ns.border} border-b border-gray-200 dark:border-gray-700 last:border-b-0`}>
                  {/* En-tête niveau (bannière colorée) avec bouton impression groupée */}
                  <NiveauBanner
                    niveau={niveau}
                    nbItems={releves.length}
                    itemLabel="relevé"
                    collapsed={isCollapsed}
                    onToggle={() => toggleGroup(niveau)}
                    rightContent={
                      <button
                        onClick={(e) => { e.stopPropagation(); handleBulkPrint(releves, `Niveau ${niveau}`); }}
                        title={`Imprimer les ${releves.length} relevés du niveau ${niveau} en une seule action`}
                        className="text-xs px-3 py-1.5 rounded-md bg-white/20 hover:bg-white/30 backdrop-blur-sm font-semibold flex items-center gap-1.5 transition"
                      >
                        🖨️ Imprimer tout ({releves.length})
                      </button>
                    }
                  />
                  {/* Liste des relevés du groupe */}
                  {!isCollapsed && (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {releves.map((releve) => (
                        <div key={releve.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <div className="flex items-start justify-between">
                            {/* Infos du relevé */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                      <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {releve.code_releve}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusLabels[releve.status]?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}>
                        {statusLabels[releve.status]?.icon}
                        <span className="ml-1">{statusLabels[releve.status]?.label || releve.status}</span>
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        releve.type_examen === 'rattrapage'
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {releve.type_examen === 'rattrapage' ? 'Rattrapage' : 'Normal'}
                      </span>
                      {releve.format_releve && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
                          {releve.format_releve.replace('manuscrit_', '').replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {releve.matiere_code} - <span className="font-semibold text-blue-600 dark:text-blue-400">{releve.matiere_nom}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {releve.niveau} | {releve.classes || releve.option} | {releve.semestre} | {releve.annee_academique}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                      {(() => {
                        const poles = getPolesFromClasses(releve.classes || '');
                        if (poles.length === 0) return null;
                        return poles.map(p => (
                          <span key={p} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 rounded font-medium">
                            <Sparkles size={10} /> {p}
                          </span>
                        ));
                      })()}
                      {releve.enseignant_nom && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded">
                          Formateur: <strong>{releve.enseignant_nom}</strong>
                        </span>
                      )}
                      {releve.genere_par_nom && (
                        <span className="text-gray-400 dark:text-gray-500">
                          Généré par: {releve.genere_par_nom}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400 flex-wrap gap-y-1">
                      <span>{releve.nombre_copies} copies</span>
                      <span>{releve.nombre_pages} pages</span>
                      <span className="flex items-center gap-1" title="Date de génération">
                        <Clock size={12} />
                        Généré : {formatDate(releve.date_generation)}
                      </span>
                      {releve.date_scan && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 rounded" title="Date d'upload du scan">
                          <Clock size={12} /> Uploadé : {formatDate(releve.date_scan)}
                        </span>
                      )}
                      {releve.scanne_par_nom && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded" title="Utilisateur qui a uploadé le scan">
                          <User size={12} /> Par : <strong>{releve.scanne_par_nom}</strong>
                        </span>
                      )}
                      {releve.copies_manquantes > 0 && (
                        <span className="text-red-600 flex items-center">
                          <AlertTriangle size={12} className="mr-1" />
                          {releve.copies_manquantes} manquantes
                        </span>
                      )}
                      {releve.lignes_a_verifier > 0 && (
                        <span className="text-orange-600 flex items-center">
                          <AlertTriangle size={12} className="mr-1" />
                          {releve.lignes_a_verifier} à vérifier
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Boutons d'actions — disposition horizontale compacte */}
                  <div className="flex items-center gap-1 ml-4 flex-shrink-0 flex-wrap justify-end">

                    {/* ── PDF ─────────────────────────────────── */}
                    <button
                      onClick={() => handleDownloadPDF(releve)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                      title="Télécharger le PDF"
                    >
                      <Download size={14} /> PDF
                    </button>
                    <button
                      onClick={() => handlePreviewPDF(releve)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Aperçu du PDF"
                    >
                      <ExternalLink size={14} /> Aperçu
                    </button>

                    {/* ── Séparateur ─── */}
                    <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-0.5" />

                    {/* ── Upload / Re-upload ───────────────────── */}
                    <button
                      onClick={() => setSelectedReleveForUpload(releve)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        ['scanne', 'traite', 'verifie'].includes(releve.status)
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                          : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50'
                      }`}
                      title={['scanne', 'traite', 'verifie'].includes(releve.status) ? 'Re-uploader le scan (remplacer)' : 'Uploader le scan rempli'}
                    >
                      <Upload size={14} />
                      {['scanne', 'traite', 'verifie'].includes(releve.status) ? 'Re-upload' : 'Upload'}
                    </button>

                    {/* ── Lancer OCR ──────────────────────────── */}
                    {releve.status === 'scanne' && !ocrEnCours.has(releve.id) && (
                      <button
                        onClick={() => handleLancerOCR(releve)}
                        disabled={ocrLoading}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 disabled:opacity-50 transition-colors"
                        title="Lancer le traitement OCR"
                      >
                        {ocrLoading ? <Loader size={14} className="animate-spin" /> : <Play size={14} />}
                        OCR
                      </button>
                    )}
                    {ocrEnCours.has(releve.id) && (
                      <span className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-lg animate-pulse">
                        <Loader size={14} className="animate-spin" />
                        OCR {ocrTimers[releve.id] ? formatTimer(ocrTimers[releve.id]) : '...'}
                      </span>
                    )}

                    {/* ── Saisie Rapide ───────────────────────── */}
                    {['scanne', 'traite'].includes(releve.status) && (
                      <button
                        onClick={() => navigate(`/ocr/saisie-rapide-v2/${releve.id}`)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                        title="Saisie manuelle rapide"
                      >
                        <Zap size={14} /> Saisie
                      </button>
                    )}

                    {/* ── Vérifier ────────────────────────────── */}
                    {['traite', 'verifie'].includes(releve.status) && (
                      <button
                        onClick={() => navigate(`/ocr/verification-correction-v2/${releve.id}`)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                        title="Vérifier et corriger les notes OCR"
                      >
                        <Eye size={14} /> Vérifier
                      </button>
                    )}

                    {/* ── Statut Validé ───────────────────────── */}
                    {releve.status === 'valide' && (
                      <span className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg">
                        <CheckCircle2 size={14} /> Validé
                      </span>
                    )}

                    {/* ── Séparateur ─── */}
                    <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-0.5" />

                    {/* ── Ajouter Copie ────────────────────────── */}
                    <button
                      onClick={() => {
                        setSelectedReleveForAddCopie(releve);
                        setAddCopieCode('');
                        setAddCopieConflit(null);
                      }}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-lg hover:bg-teal-200 dark:hover:bg-teal-900/50 transition-colors"
                      title="Ajouter manuellement un code copie"
                    >
                      <Plus size={14} /> Copie
                    </button>

                    {/* ── Détails ─────────────────────────────── */}
                    <button
                      onClick={() => setSelectedReleveForDetails(releve)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      title="Voir les détails et statistiques OCR"
                    >
                      <FileSearch size={14} /> Détails
                    </button>

                    {/* ── Supprimer ───────────────────────────── */}
                    <button
                      onClick={() => setSelectedReleveForDelete(releve)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Supprimer ce relevé"
                    >
                      <Trash2 size={14} /> Supprimer
                    </button>
                          </div>
                        </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
        )}

        {/* Onglet 2 : Relevés traités / vérifiés / validés */}
        {activeListTab === 'traites' && (
        <div className="p-4">
            {/* Sous-onglets session : Toutes / Ordinaire / Rattrapage */}
            <div className="mb-3 flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700 pb-2">
              {([
                { key: '', label: 'Toutes', count: relevesTraitesAll.length, color: 'gray' },
                { key: 'normal', label: 'Session ordinaire', count: relevesTraitesAll.filter(r => (r.type_examen || 'normal') === 'normal').length, color: 'blue' },
                { key: 'rattrapage', label: 'Session de rattrapage', count: relevesTraitesAll.filter(r => (r.type_examen || 'normal') === 'rattrapage').length, color: 'orange' },
              ] as const).map(opt => {
                const active = traiteSessionFilter === opt.key;
                const colorCls = active
                  ? (opt.color === 'blue' ? 'bg-blue-600 text-white border-blue-600'
                    : opt.color === 'orange' ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-gray-700 text-white border-gray-700')
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';
                return (
                  <button
                    key={opt.key || 'all'}
                    onClick={() => setTraiteSessionFilter(opt.key as any)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors inline-flex items-center gap-1.5 ${colorCls}`}
                  >
                    {opt.label}
                    <span className={`px-1.5 py-0 rounded-full text-[10px] font-bold ${
                      active ? 'bg-white/25' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {opt.count}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Filtres relevés traités */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="flex-1 min-w-[200px] relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={traiteSearch}
                  onChange={(e) => setTraiteSearch(e.target.value)}
                  placeholder="Rechercher par code, matière, formateur, classe..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <select
                value={traiteStatutFilter}
                onChange={(e) => setTraiteStatutFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Tous statuts</option>
                <option value="traite">Traité</option>
                <option value="verifie">Vérifié</option>
                <option value="valide">Validé</option>
              </select>
              <select
                value={traiteNiveauFilter}
                onChange={(e) => setTraiteNiveauFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Tous niveaux</option>
                {niveauxTraites.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              {/* Toggle compact impair/pair pour les traités — même UX que
                  la liste principale, avec compteurs. */}
              {(() => {
                const nbI = relevesTraitesAll.filter(r => semestreParity(r.semestre) === 'impair').length;
                const nbP = relevesTraitesAll.filter(r => semestreParity(r.semestre) === 'pair').length;
                return (
                  <div className={`inline-flex rounded-lg border overflow-hidden text-xs ${
                    verrouParity ? 'border-red-400 dark:border-red-700 ring-1 ring-red-200 dark:ring-red-900' : 'border-gray-300 dark:border-gray-600'
                  }`}
                    title={verrouParity ? 'Verrouillé par admin' : undefined}
                  >
                    {verrouParity && <Lock size={10} className="text-red-500 self-center mx-1 shrink-0" />}
                    <button onClick={() => !verrouParity && setTraiteSemestreParity('')}
                      disabled={!!verrouParity}
                      className={`px-2 py-1.5 font-medium ${!traiteSemestreParity ? 'bg-gray-800 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200'} ${verrouParity ? 'opacity-40 cursor-not-allowed' : ''}`}>
                      Tous ({nbI + nbP})
                    </button>
                    <button onClick={() => !verrouParity && setTraiteSemestreParity('impair')}
                      disabled={!!verrouParity && verrouParity !== 'impair'}
                      className={`px-2 py-1.5 font-medium border-l border-gray-300 dark:border-gray-600 ${traiteSemestreParity === 'impair' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200'} ${verrouParity && verrouParity !== 'impair' ? 'opacity-40 cursor-not-allowed' : ''}`}>
                      Impairs ({nbI})
                    </button>
                    <button onClick={() => !verrouParity && setTraiteSemestreParity('pair')}
                      disabled={!!verrouParity && verrouParity !== 'pair'}
                      className={`px-2 py-1.5 font-medium border-l border-gray-300 dark:border-gray-600 ${traiteSemestreParity === 'pair' ? 'bg-purple-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200'} ${verrouParity && verrouParity !== 'pair' ? 'opacity-40 cursor-not-allowed' : ''}`}>
                      Pairs ({nbP})
                    </button>
                  </div>
                );
              })()}
              {(traiteSearch || traiteStatutFilter || traiteNiveauFilter || traiteSessionFilter || traiteSemestreParity) && (
                <button
                  onClick={() => { setTraiteSearch(''); setTraiteStatutFilter(''); setTraiteNiveauFilter(''); setTraiteSessionFilter(''); setTraiteSemestreParity(''); }}
                  className="px-2 py-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Réinitialiser
                </button>
              )}
              <span className="text-[10px] text-gray-400">{relevesTraites.length}/{relevesTraitesAll.length}</span>
            </div>

            {relevesTraites.length === 0 ? (
              <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                <CheckCircle2 size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p className="text-lg font-medium">Aucun relevé traité</p>
                <p className="text-sm mt-1">Les relevés apparaîtront ici dès qu'ils passent au statut Traité, Vérifié ou Validé</p>
              </div>
            ) : (
              <div>
                {relevesTraitesGroupes.map(({ niveau, releves }) => {
                  const key = 'traite-' + niveau;
                  const isCollapsed = collapsedGroups[key];
                  const ns = getNiveauStyle(niveau);
                  return (
                    <div key={key} className={`border-l-4 ${ns.border} border-b border-gray-200 dark:border-gray-700 last:border-b-0`}>
                      <NiveauBanner
                        niveau={niveau}
                        nbItems={releves.length}
                        itemLabel="relevé traité"
                        collapsed={isCollapsed}
                        onToggle={() => toggleGroup(key)}
                        rightContent={
                          <button
                            onClick={(e) => { e.stopPropagation(); handleBulkPrint(releves, `Traités ${niveau}`); }}
                            title={`Imprimer les ${releves.length} relevés traités du niveau ${niveau}`}
                            className="text-xs px-3 py-1.5 rounded-md bg-white/20 hover:bg-white/30 backdrop-blur-sm font-semibold flex items-center gap-1.5 transition"
                          >
                            🖨️ Imprimer tout ({releves.length})
                          </button>
                        }
                      />
                      {!isCollapsed && (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {releves.map((releve) => (
                            <div key={releve.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{releve.code_releve}</span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabels[releve.status]?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
                                      {statusLabels[releve.status]?.icon}
                                      <span className="ml-1">{statusLabels[releve.status]?.label || releve.status}</span>
                                    </span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      releve.type_examen === 'rattrapage'
                                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}>
                                      {releve.type_examen === 'rattrapage' ? 'Rattrapage' : 'Normal'}
                                    </span>
                                  </div>
                                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                    {releve.matiere_code} - <span className="font-semibold text-blue-600 dark:text-blue-400">{releve.matiere_nom}</span>
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {releve.niveau} | {releve.classes || releve.option} | {releve.semestre} | {releve.annee_academique}
                                  </div>
                                  <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                                    {releve.enseignant_nom && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded">
                                        Formateur: <strong>{releve.enseignant_nom}</strong>
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400 flex-wrap gap-y-1">
                                    <span>{releve.nombre_copies} copies</span>
                                    <span>{releve.nombre_pages} pages</span>
                                    <span className="flex items-center gap-1" title="Date de génération"><Clock size={12} />Généré : {formatDate(releve.date_generation)}</span>
                                    {releve.date_scan && (
                                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 rounded" title="Date d'upload du scan">
                                        <Clock size={12} /> Uploadé : {formatDate(releve.date_scan)}
                                      </span>
                                    )}
                                    {releve.scanne_par_nom && (
                                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded" title="Utilisateur qui a uploadé le scan">
                                        <User size={12} /> Par : <strong>{releve.scanne_par_nom}</strong>
                                      </span>
                                    )}
                                    {releve.total_notes_detectees > 0 && (
                                      <span className="text-green-600 flex items-center gap-1">
                                        <CheckCircle2 size={12} /> {releve.total_notes_detectees} notes
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Boutons d'actions */}
                                <div className="flex items-center gap-1 ml-4 flex-shrink-0 flex-wrap justify-end">
                                  <button
                                    onClick={() => setConsultReleve(releve)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                                    title="Voir le scan et les notes saisies côte à côte"
                                  >
                                    <Eye size={14} /> Consulter
                                  </button>
                                  <button
                                    onClick={() => setSelectedReleveForRetour(releve)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
                                    title="Retourner ce relevé dans la liste 'En cours' pour refaire l'upload et l'OCR"
                                  >
                                    <RotateCcw size={14} /> Retour en cours
                                  </button>
                                  <button
                                    onClick={() => handleDownloadPDF(releve)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                    title="Télécharger le PDF"
                                  >
                                    <Download size={14} /> PDF
                                  </button>
                                  {['traite', 'verifie'].includes(releve.status) && (
                                    <button
                                      onClick={() => navigate(`/ocr/verification-correction-v2/${releve.id}`)}
                                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                                      title="Vérifier et corriger les notes OCR"
                                    >
                                      <FileSearch size={14} /> Vérifier
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setSelectedReleveForDetails(releve)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                    title="Voir les détails"
                                  >
                                    <FileSearch size={14} /> Détails
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </div>
        )}
      </div>

      {/* ========== MODAL CONSULTATION SCAN + NOTES ========== */}
      {consultReleve && (
        <ConsultationScanModal
          releve={consultReleve}
          onClose={() => setConsultReleve(null)}
        />
      )}

      {/* ========== MODALS ========== */}

      {/* Modal Upload Scan */}
      {selectedReleveForUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Upload size={20} className="text-purple-600" />
              Uploader le scan - {selectedReleveForUpload.code_releve}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fichier scanné (PDF ou images)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Format recommandé : PDF multi-pages ou images scannées en 300 DPI couleur
              </p>
            </div>

            {uploadFile && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                <p className="font-medium text-gray-700 dark:text-gray-300">Fichier sélectionné :</p>
                <p className="text-gray-600 dark:text-gray-400">{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} Mo)</p>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={handleUploadScan}
                disabled={uploadLoading || !uploadFile}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploadLoading ? (
                  <Loader className="animate-spin mr-2" size={18} />
                ) : (
                  <Upload className="mr-2" size={18} />
                )}
                Uploader
              </button>
              <button
                onClick={() => {
                  setSelectedReleveForUpload(null);
                  setUploadFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Détails */}
      {selectedReleveForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <FileSearch size={20} className="text-blue-600" />
                Détails - {selectedReleveForDetails.code_releve}
              </h3>
              <button
                onClick={() => setSelectedReleveForDetails(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Matière</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.matiere_code} - {selectedReleveForDetails.matiere_nom}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Niveau / Classe(s)</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.niveau} - {selectedReleveForDetails.classes || selectedReleveForDetails.option}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Type d'examen</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.type_examen === 'rattrapage' ? 'Rattrapage' : 'Session ordinaire'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Année académique</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.annee_academique}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Statut</span>
                  <p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabels[selectedReleveForDetails.status]?.color}`}>
                      {statusLabels[selectedReleveForDetails.status]?.icon}
                      <span className="ml-1">{statusLabels[selectedReleveForDetails.status]?.label}</span>
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Nombre de copies</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.nombre_copies}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Nombre de pages</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.nombre_pages}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Date de génération</span>
                  <p className="font-medium dark:text-gray-200">{formatDate(selectedReleveForDetails.date_generation)}</p>
                </div>
                {selectedReleveForDetails.format_releve && (
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Format</span>
                    <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.format_releve.replace('manuscrit_', '').replace('_', ' ')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Statistiques OCR si traité */}
            {['traite', 'verifie', 'valide'].includes(selectedReleveForDetails.status) && (
              <div className="mt-6 pt-4 border-t dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <BarChart3 size={16} className="text-blue-500" />
                  Résultats OCR
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Notes détectées</span>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{selectedReleveForDetails.total_notes_detectees || 0}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Confiance haute</span>
                    <p className="text-2xl font-bold text-green-600">{selectedReleveForDetails.notes_confiance_haute || 0}</p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 dark:text-gray-400">À vérifier</span>
                    <p className="text-2xl font-bold text-orange-600">{selectedReleveForDetails.notes_a_verifier || selectedReleveForDetails.lignes_a_verifier || 0}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Copies manquantes</span>
                    <p className="text-2xl font-bold text-red-600">{selectedReleveForDetails.copies_manquantes || 0}</p>
                  </div>
                </div>

                {/* Temps de traitement OCR */}
                {dernierTraitement && (
                  <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg p-4">
                    <h5 className="text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Clock size={13} /> Traitement OCR
                    </h5>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Durée totale</p>
                        <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                          {dernierTraitement.duree_secondes !== undefined && dernierTraitement.duree_secondes !== null
                            ? dernierTraitement.duree_secondes >= 60
                              ? `${Math.floor(dernierTraitement.duree_secondes / 60)}m ${Math.round(dernierTraitement.duree_secondes % 60)}s`
                              : `${dernierTraitement.duree_secondes.toFixed(1)}s`
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Pages traitées</p>
                        <p className="text-xl font-bold text-gray-700 dark:text-gray-200">{dernierTraitement.nombre_pages_traitees ?? '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Confiance moyenne</p>
                        <p className="text-xl font-bold text-gray-700 dark:text-gray-200">
                          {dernierTraitement.confiance_moyenne
                            ? `${Number(dernierTraitement.confiance_moyenne).toFixed(1)}%`
                            : '—'}
                        </p>
                      </div>
                    </div>
                    {dernierTraitement.date_debut && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        Lancé le {new Date(dernierTraitement.date_debut).toLocaleString('fr-FR')}
                        {dernierTraitement.lance_par_nom ? ` par ${dernierTraitement.lance_par_nom}` : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => handleDownloadPDF(selectedReleveForDetails)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Download size={18} className="mr-2" />
                Télécharger PDF
              </button>
              <button
                onClick={() => setSelectedReleveForDetails(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter Copie */}
      {selectedReleveForAddCopie && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="bg-teal-600 px-6 py-4 flex items-center gap-3">
              <Plus size={20} className="text-white" />
              <div>
                <h3 className="text-lg font-bold text-white">Ajouter une copie au relevé</h3>
                <p className="text-teal-100 text-xs mt-0.5">
                  {selectedReleveForAddCopie.code_releve} — {selectedReleveForAddCopie.matiere_nom}
                </p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Code copie
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={addCopieCode}
                    onChange={(e) => {
                      setAddCopieCode(e.target.value.toUpperCase());
                      setAddCopieConflit(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && addCopieCode.trim()) {
                        handleAjouterCopie(false);
                      }
                    }}
                    placeholder="Ex: SOR01234"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-lg"
                    autoFocus
                  />
                  <button
                    disabled={ajoutCopieLoading || !addCopieCode.trim()}
                    onClick={() => handleAjouterCopie(false)}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium"
                  >
                    {ajoutCopieLoading ? <Loader size={16} className="animate-spin" /> : 'Ajouter'}
                  </button>
                </div>
              </div>

              {/* Conflit détecté */}
              {addCopieConflit && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-amber-800 dark:text-amber-200 text-sm">Conflit de matière</h4>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        La copie <strong>{addCopieConflit.copie_code}</strong> est actuellement associée à une autre matière.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <p className="text-red-500 dark:text-red-400 font-semibold mb-1">Matière actuelle</p>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">{addCopieConflit.matiere_actuelle.nom}</p>
                      <p className="text-gray-500 font-mono">{addCopieConflit.matiere_actuelle.code}</p>
                      {addCopieConflit.enseignant_actuel && (
                        <p className="text-gray-500 mt-1">Formateur: {addCopieConflit.enseignant_actuel}</p>
                      )}
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-3">
                      <p className="text-green-500 dark:text-green-400 font-semibold mb-1">Matière cible (ce relevé)</p>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">{addCopieConflit.matiere_releve.nom}</p>
                      <p className="text-gray-500 font-mono">{addCopieConflit.matiere_releve.code}</p>
                    </div>
                  </div>

                  {addCopieConflit.etudiant && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded p-2 border border-gray-200 dark:border-gray-700">
                      <UserCheck size={12} className="inline mr-1" />
                      Étudiant: <strong>{addCopieConflit.etudiant.nom}</strong> ({addCopieConflit.etudiant.matricule}) — {addCopieConflit.etudiant.classe}
                    </div>
                  )}

                  <button
                    disabled={ajoutCopieLoading}
                    onClick={() => handleAjouterCopie(true)}
                    className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium text-sm"
                  >
                    {ajoutCopieLoading ? <Loader size={14} className="inline animate-spin mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
                    Forcer : dissocier et réassocier à ce relevé
                  </button>
                </div>
              )}
            </div>

            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setSelectedReleveForAddCopie(null);
                  setAddCopieCode('');
                  setAddCopieConflit(null);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Retour en cours */}
      {selectedReleveForRetour && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
              <RotateCcw size={20} className="text-orange-600" />
              Retourner le relevé en cours
            </h3>
            <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg">
              <p className="text-sm text-orange-800 dark:text-orange-300">
                Relevé <strong>{selectedReleveForRetour.code_releve}</strong> ({statusLabels[selectedReleveForRetour.status]?.label})
              </p>
              <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
                {selectedReleveForRetour.matiere_code} — {selectedReleveForRetour.matiere_nom}
              </p>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Les notes OCR détectées et les validations seront <strong>supprimées</strong>.
              Le relevé retournera dans la liste "En cours" pour permettre un nouvel upload et re-traitement OCR.
            </p>

            <div className="mb-3">
              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={retourSupprimerScan}
                  onChange={e => setRetourSupprimerScan(e.target.checked)}
                  className="mt-0.5 rounded"
                />
                <span>
                  Supprimer également le fichier scanné existant
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {retourSupprimerScan
                      ? 'Le relevé passera à "Généré". Il faudra re-uploader un scan.'
                      : 'Le scan sera conservé. Le relevé passera à "Scanné".'}
                  </span>
                </span>
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Motif du retour (optionnel)
              </label>
              <input
                type="text"
                value={retourMotif}
                onChange={e => setRetourMotif(e.target.value)}
                placeholder="Ex: OCR incorrect, scan de mauvaise qualité..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setSelectedReleveForRetour(null); setRetourSupprimerScan(false); setRetourMotif(''); }}
                disabled={retournerLoading}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Annuler
              </button>
              <button
                onClick={handleRetournerEnCours}
                disabled={retournerLoading}
                className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {retournerLoading ? <Loader size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                Confirmer le retour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression */}
      {selectedReleveForDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirmer la suppression</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Cette action est irréversible</p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Êtes-vous sûr de vouloir supprimer le relevé <strong>{selectedReleveForDelete.code_releve}</strong> ?
              Tous les fichiers et données OCR associés seront définitivement supprimés.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleDeleteReleve}
                disabled={deleteLoading}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteLoading ? (
                  <Loader className="animate-spin mr-2" size={18} />
                ) : (
                  <Trash2 className="mr-2" size={18} />
                )}
                Supprimer
              </button>
              <button
                onClick={() => setSelectedReleveForDelete(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal upload en lot par QR */}
      <UploadBatchParQRModal
        open={showUploadBatchQR}
        onClose={() => setShowUploadBatchQR(false)}
        type="correction"
      />

      {/* Modal historique imports */}
      <HistoriqueImportsModal
        open={showHistoriqueImports}
        onClose={() => setShowHistoriqueImports(false)}
        typeFilter="correction"
      />

    </div>
  );
};

// ============================================================================
// Modal de consultation : scan + notes saisies côte à côte
// ============================================================================

interface ConsultationScanModalProps {
  releve: ReleveCorrection;
  onClose: () => void;
}

const ConsultationScanModal: React.FC<ConsultationScanModalProps> = ({ releve, onClose }) => {
  const navigate = useNavigate();
  const { data: lignes = [], isLoading } = useGetLignesOCRCorrectionQuery({ releveId: releve.id });

  const scanUrl = releve.fichier_scan || null;

  // Séparer les lignes détectées vs non détectées
  const lignesTriees = useMemo(() => {
    return [...lignes].sort((a, b) => {
      if (a.numero_page !== b.numero_page) return a.numero_page - b.numero_page;
      return a.numero_ligne - b.numero_ligne;
    });
  }, [lignes]);

  const stats = useMemo(() => {
    const total = lignes.length;
    const saisies = lignes.filter(l => l.note_finale !== null && l.note_finale !== undefined).length;
    const absents = lignes.filter(l => l.absent_valide).length;
    const aVerifier = lignes.filter(l => l.necessite_verification && !l.verifie).length;
    return { total, saisies, absents, aVerifier };
  }, [lignes]);

  const formatNote = (note?: number | string | null): string => {
    if (note === null || note === undefined || note === '') return '—';
    const n = typeof note === 'number' ? note : parseFloat(String(note));
    if (isNaN(n)) return '—';
    return n.toFixed(2).replace('.', ',');
  };

  const handlePrint = () => {
    const rows = lignesTriees.map((l, i) => {
      const note = l.absent_valide ? 'ABS' : formatNote(l.note_finale);
      const matricule = l.etudiant_matricule || '—';
      const nom = l.etudiant_nom || '—';
      const copie = l.copie_code || l.code_copie_detecte || '—';
      return `<tr style="background:${i % 2 === 0 ? '#fff' : '#f8f9fa'};">
        <td style="border:1px solid #666;padding:4px 6px;text-align:center;">${i + 1}</td>
        <td style="border:1px solid #666;padding:4px 6px;font-family:monospace;">${copie}</td>
        <td style="border:1px solid #666;padding:4px 6px;font-family:monospace;">${matricule}</td>
        <td style="border:1px solid #666;padding:4px 6px;">${nom}</td>
        <td style="border:1px solid #666;padding:4px 6px;text-align:center;font-weight:bold;${l.absent_valide ? 'color:#B45309;' : ''}">${note}</td>
      </tr>`;
    }).join('');

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const NAVY = '#1A2E4A';

    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>Consultation - ${releve.code_releve}</title>
<style>
  @page { size: A4; margin: 10mm; }
  @media print { .no-print { display: none !important; } }
  * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: 'Times New Roman', serif; font-size: 10pt; color: #1a1a1a; background: #fff; padding: 10px; }
  table { width: 100%; border-collapse: collapse; }
  .scan-frame { width: 100%; height: 600px; border: 1px solid #ccc; }
</style></head><body>

<div style="display:flex;align-items:center;justify-content:center;gap:16px;margin-bottom:8px;">
  <img src="/logo.png" alt="Logo" style="height:55px;" onerror="this.style.display='none'" />
  <div style="text-align:center;">
    <div style="font-size:13pt;font-weight:bold;letter-spacing:1px;">INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE</div>
    <div style="font-size:8pt;font-style:italic;">Agrément définitif par l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
  </div>
</div>

<div style="text-align:center;margin:10px 0;padding:8px;background:${NAVY};color:#FFD700;font-size:13pt;font-weight:bold;letter-spacing:2px;border-radius:4px;">
  CONSULTATION RELEVÉ DE CORRECTION
</div>

<div style="margin:8px 0;font-size:9pt;">
  <table style="width:100%;">
    <tr>
      <td style="padding:3px 6px;"><strong>Code :</strong> ${releve.code_releve}</td>
      <td style="padding:3px 6px;"><strong>Matière :</strong> ${releve.matiere_code} — ${releve.matiere_nom}</td>
      <td style="padding:3px 6px;"><strong>Niveau :</strong> ${releve.niveau}</td>
    </tr>
    <tr>
      <td style="padding:3px 6px;"><strong>Classes :</strong> ${releve.classes || releve.option}</td>
      <td style="padding:3px 6px;"><strong>Semestre :</strong> ${releve.semestre} / ${releve.annee_academique}</td>
      <td style="padding:3px 6px;"><strong>Formateur :</strong> ${releve.enseignant_nom || '—'}</td>
    </tr>
    <tr>
      <td style="padding:3px 6px;"><strong>Type :</strong> ${releve.type_examen === 'rattrapage' ? 'Rattrapage' : 'Normal'}</td>
      <td style="padding:3px 6px;"><strong>Nombre de copies :</strong> ${releve.nombre_copies}</td>
      <td style="padding:3px 6px;"><strong>Statut :</strong> ${statusLabels[releve.status]?.label || releve.status}</td>
    </tr>
  </table>
</div>

<h3 style="margin-top:14px;margin-bottom:6px;font-size:11pt;border-bottom:2px solid ${NAVY};padding-bottom:3px;">Notes saisies (${stats.total})</h3>

<table>
  <thead>
    <tr style="background:${NAVY};color:#FFD700;">
      <th style="border:1px solid #666;padding:5px 6px;width:30px;">N°</th>
      <th style="border:1px solid #666;padding:5px 6px;width:110px;">Code copie</th>
      <th style="border:1px solid #666;padding:5px 6px;width:110px;">Matricule</th>
      <th style="border:1px solid #666;padding:5px 6px;">Nom &amp; Prénom</th>
      <th style="border:1px solid #666;padding:5px 6px;width:60px;">Note /20</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>

<div style="margin-top:6px;font-size:8pt;color:#666;">
  Total : <strong>${stats.total}</strong> &nbsp;|&nbsp;
  Saisies : <strong style="color:#047857;">${stats.saisies}</strong> &nbsp;|&nbsp;
  Absents : <strong style="color:#B45309;">${stats.absents}</strong> &nbsp;|&nbsp;
  À vérifier : <strong style="color:#DC2626;">${stats.aVerifier}</strong>
</div>

<div style="margin-top:30px;display:flex;justify-content:space-between;align-items:flex-end;">
  <div style="font-size:9pt;">
    <div>Fait à Pointe-Noire, le ${today}</div>
  </div>
  <div style="text-align:center;">
    <div style="font-size:9pt;font-weight:bold;">Le Chef de Service de la Scolarité et des Examens</div>
    <div style="margin-top:40px;border-top:1px solid #000;padding-top:4px;min-width:250px;">
      <strong>Donald Christ MBEMBA LOKO</strong>
    </div>
  </div>
</div>

</body></html>`;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-[1600px] h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <Eye size={22} className="text-emerald-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {releve.code_releve}
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {releve.matiere_code} — <span className="font-medium text-blue-600 dark:text-blue-400">{releve.matiere_nom}</span>
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusLabels[releve.status]?.color}`}>
              {statusLabels[releve.status]?.icon}
              <span className="ml-1">{statusLabels[releve.status]?.label}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={!lignes.length}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Printer size={16} /> Imprimer
            </button>
            {scanUrl && (
              <a
                href={scanUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <ExternalLink size={16} /> Ouvrir scan
              </a>
            )}
            {/* Rééditer notes + relancer validation (relevés au moins traités) */}
            {['traite', 'verifie', 'valide'].includes(releve.status) && (
              <button
                onClick={() => {
                  onClose();
                  navigate(`/ocr/verification-correction-v2/${releve.id}`);
                }}
                title="Modifier les notes ligne par ligne et relancer la validation (utile après correction d'erreurs OCR)"
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                <RefreshCw size={16} /> Rééditer les notes
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-4 gap-2 px-5 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 text-center text-xs">
          <div>
            <div className="text-gray-500 dark:text-gray-400">Total</div>
            <div className="font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
          </div>
          <div>
            <div className="text-gray-500 dark:text-gray-400">Saisies</div>
            <div className="font-bold text-green-600 dark:text-green-400">{stats.saisies}</div>
          </div>
          <div>
            <div className="text-gray-500 dark:text-gray-400">Absents</div>
            <div className="font-bold text-amber-600 dark:text-amber-400">{stats.absents}</div>
          </div>
          <div>
            <div className="text-gray-500 dark:text-gray-400">À vérifier</div>
            <div className="font-bold text-red-600 dark:text-red-400">{stats.aVerifier}</div>
          </div>
        </div>

        {/* Body : Scan + Notes */}
        <div className="flex-1 flex overflow-hidden">
          {/* Scan à gauche */}
          <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 overflow-hidden flex flex-col">
            <div className="px-3 py-1.5 bg-gray-200 dark:bg-gray-800 text-xs font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-700 flex-shrink-0">
              📄 Fichier scanné {scanUrl ? '' : '(non disponible)'}
            </div>
            <div className="flex-1 overflow-auto">
              {scanUrl ? (
                <iframe
                  src={scanUrl}
                  title="Scan"
                  className="w-full h-full bg-white"
                  style={{ border: 'none', minHeight: '100%' }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <FileText size={48} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Aucun fichier scanné disponible</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes à droite */}
          <div className="w-1/2 overflow-hidden flex flex-col">
            <div className="px-3 py-1.5 bg-gray-200 dark:bg-gray-800 text-xs font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-700 flex-shrink-0">
              📝 Notes saisies ({stats.total})
            </div>
            <div className="flex-1 overflow-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader className="animate-spin text-blue-600" size={32} />
                </div>
              ) : lignesTriees.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <AlertTriangle size={40} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Aucune note saisie pour ce relevé</p>
                  </div>
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 z-10">
                    <tr>
                      <th className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-300 w-10">N°</th>
                      <th className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-300">Code copie</th>
                      <th className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-300">Matricule</th>
                      <th className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-300">Nom</th>
                      <th className="text-center px-2 py-2 font-semibold text-gray-600 dark:text-gray-300 w-16">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignesTriees.map((l, i) => {
                      const rowBg = l.necessite_verification && !l.verifie
                        ? 'bg-red-50 dark:bg-red-900/10'
                        : l.absent_valide
                          ? 'bg-amber-50 dark:bg-amber-900/10'
                          : i % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-800/30';
                      return (
                        <tr key={l.id} className={`border-t border-gray-200 dark:border-gray-700 ${rowBg}`}>
                          <td className="px-2 py-1.5 text-gray-400">{i + 1}</td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">{l.copie_code || l.code_copie_detecte || '—'}</td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">{l.etudiant_matricule || '—'}</td>
                          <td className="px-2 py-1.5">{l.etudiant_nom || <span className="text-gray-400 italic">Non associé</span>}</td>
                          <td className="px-2 py-1.5 text-center font-bold">
                            {l.absent_valide ? (
                              <span className="text-amber-600 dark:text-amber-400">ABS</span>
                            ) : l.note_finale !== null && l.note_finale !== undefined ? (
                              <span className="text-gray-900 dark:text-gray-100">{formatNote(l.note_finale)}</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />
    </div>
  );
};

export default GenerationReleveCorrectionV2;
